import React, { useContext, useMemo } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import createList, { ListContext, useList } from "modules/list";
import ListActions from "modules/list/actions";

import Loader from "components/ui/Loader";
import EmptyResults from "components/ui/EmptyResults";
import Fields from "components/ui/Fields";

const LoaderWrap = styled.div`
  min-height: 50px;
  display: flex;
  align-items: center;
`;

export default function createAutocompleteComponent({
  Component = Fields.Select,
  dataFetcher,
  parseOptions = (result) => result,
  schema,
  listActionsModule,
  identifier,
  loadStrategy = "all",
  ...rest
}) {
  const listActions =
    listActionsModule ||
    new ListActions({
      ...rest,
      schema,
      dataFetcher,
      identifier,
      loadStrategy,
    });
  const ListModule = createList({
    loadStrategy,
    actions: listActions,
    schema,
  });

  function onSearch(module, searchTerm) {
    return function thunk(dispatch) {
      dispatch(
        listActions.changeQuery({ name: "search", value: searchTerm, module })
      );
    };
  }

  function NotFoundContent() {
    const listContext = useList();

    return listContext.isLoading ? (
      <LoaderWrap>
        <Loader small />
      </LoaderWrap>
    ) : (
      <EmptyResults />
    );
  }

  function SelectAutocomplete({ onSearch, ...rest }) {
    const { items } = useList();
    const options = useMemo(() => {
      return parseOptions({ items });
    }, [items]);

    return (
      <Component
        filterOption={false}
        notFoundContent={<NotFoundContent />}
        onSearch={onSearch}
        options={options}
        {...rest}
      />
    );
  }

  function AsyncListModule({ module, preventInitOnMount, ...rest }) {
    return (
      <ListModule module={module} preventInitOnMount={preventInitOnMount}>
        <SelectAutocomplete {...rest} />
      </ListModule>
    );
  }

  return connect(null, (dispatch, ownProps) => ({
    onSearch: (searchTerm) => dispatch(onSearch(ownProps.module, searchTerm)),
  }))(AsyncListModule);
}
